import { useMemo, useState } from 'react';

import { SegmentedControl } from '../../../cdl/SegmentedControl/SegmentedControl';
import { Text } from '../../../cdl/Text/Text';
import { GenericError } from '../../../common/Error/GenericError';
import { formatDateTime } from '../../../common/helpers/formatDateTime.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { LoadingIndicator } from '../../../common/LoadingIndicator/LoadingIndicator';
import { createOverlay } from '../../../common/Overlay/Overlay';
import { OverlayHeader } from '../../../common/Overlay/OverlayHeader';
import { Box } from '../../../common/ui/Box';
import { OfferModel } from '../../model/OfferModel';
import { buildOfferHistoryTransitions } from '../history/buildOfferHistoryTransitions';
import { OfferHistoryEmptyState } from '../history/components/OfferHistoryEmptyState';
import { OfferHistorySnapshotView } from '../history/components/OfferHistorySnapshotView';
import { OfferHistorySummaryView } from '../history/components/OfferHistorySummaryView';
import { OfferHistoryTimeline } from '../history/components/OfferHistoryTimeline';
import { useOfferHistory } from '../history/useOfferHistory';

type OfferHistoryViewMode = 'summary' | 'before' | 'after';

interface OfferHistoryOverlayProps {
    offer: OfferModel;
    onClose: () => void;
}

export const OfferHistoryOverlay = (props: OfferHistoryOverlayProps) => {
    return createOverlay(<OfferHistoryOverlayContent {...props} />);
};

const OfferHistoryOverlayContent = ({ offer, onClose }: OfferHistoryOverlayProps) => {
    const historyQuery = useOfferHistory(offer.id);
    const [selectedTransitionId, setSelectedTransitionId] = useState<string>();
    const [viewMode, setViewMode] = useState<OfferHistoryViewMode>('summary');

    const transitions = useMemo(() => {
        if (!historyQuery.data) {
            return [];
        }

        return buildOfferHistoryTransitions(historyQuery.data, offer);
    }, [historyQuery.data, offer]);

    const selectedTransition =
        transitions.find((transition) => transition.id === selectedTransitionId) ?? transitions[0];

    const selectedTransitionActor = selectedTransition?.actorName ?? translate('offer.history.actorUnknown');

    return (
        <Box minHeight="100%" paddingBottom={64}>
            <OverlayHeader title={translate('offer.history.drawerTitle')} onClose={onClose} maxContentWidth="1420px">
                <Box paddingX={24} paddingTop={24} paddingBottom={48}>
                    <Box display="flex" flexDirection="column" gap={24}>
                        {historyQuery.isPending ? <LoadingIndicator variant="full-height" /> : null}
                        {historyQuery.isError ? <GenericError variant="basic" /> : null}
                        {!historyQuery.isPending && !historyQuery.isError && !selectedTransition ? (
                            <OfferHistoryEmptyState />
                        ) : null}

                        {selectedTransition ? (
                            <>
                                <OfferHistoryTimeline
                                    transitions={transitions}
                                    selectedTransitionId={selectedTransition.id}
                                    onSelect={setSelectedTransitionId}
                                />

                                <Box display="flex" flexDirection="column" gap={12}>
                                    <Box display="flex" flexDirection="column" gap={6}>
                                        <Text variant="subtitle1">
                                            {translate('offer.history.selectedRevisionTitle')}
                                        </Text>
                                        <Text color="foreground.muted">
                                            {translate('offer.history.selectedRevisionDescription', {
                                                actor: selectedTransitionActor,
                                                timestamp: formatDateTime({ date: selectedTransition.timestamp }),
                                            })}
                                        </Text>
                                    </Box>

                                    <Box width="fit-content">
                                        <SegmentedControl<OfferHistoryViewMode>
                                            options={[
                                                {
                                                    value: 'summary',
                                                    label: translate('offer.history.mode.summary'),
                                                },
                                                {
                                                    value: 'before',
                                                    label: translate('offer.history.compare.before'),
                                                },
                                                {
                                                    value: 'after',
                                                    label: translate('offer.history.compare.after'),
                                                },
                                            ]}
                                            value={viewMode}
                                            onChange={setViewMode}
                                        />
                                    </Box>
                                </Box>

                                {viewMode === 'summary' ? (
                                    <OfferHistorySummaryView transition={selectedTransition} />
                                ) : (
                                    <OfferHistorySnapshotView
                                        offer={selectedTransition[viewMode]}
                                        actorName={selectedTransition.actorName}
                                        timestamp={selectedTransition.timestamp}
                                        revision={viewMode}
                                    />
                                )}
                            </>
                        ) : null}
                    </Box>
                </Box>
            </OverlayHeader>
        </Box>
    );
};
